<script setup lang="ts">
import { useModalManager } from "@/composables/useModalManager";
import { AuthSectionEnum, ModalEnum } from "@/types/enums";
import { ref, watch } from "vue";
import { useRoute } from "vue-router";
import BaseModal from "./BaseModal.vue";
import AuthEmail from "./auth/AuthEmail.vue";
import AuthForgotPassword from "./auth/AuthForgotPassword.vue";
import AuthPassword from "./auth/AuthPassword.vue";
import AuthResetPassword from "./auth/AuthResetPassword.vue";

const screens = {
  [AuthSectionEnum.Email]: AuthEmail,
  [AuthSectionEnum.Password]: AuthPassword,
  [AuthSectionEnum.ForgotPassword]: AuthForgotPassword,
  [AuthSectionEnum.ResetPassword]: AuthResetPassword,
};

const route = useRoute();
const { activeModal } = useModalManager();
const show = ref(false);
const header = ref("Welcome to 4C Predictions");
const activeSection = ref<AuthSectionEnum>(AuthSectionEnum.Email);
const emailExists = ref(false);
const emailAddress = ref("");

const onClose = () => {
  show.value = false;
  activeSection.value = AuthSectionEnum.Email;
  emailExists.value = false;
  emailAddress.value = "";
};

watch(
  () => activeModal.value,
  () => {
    show.value = activeModal.value == ModalEnum.AuthModal;
  },
  { immediate: true },
);

watch(
  () => show.value,
  () => {
    if (!show.value) {
      onClose();
    }
  },
  { immediate: true },
);

watch(
  () => route.query,
  (query) => {
    if (query.reset_password && query.email) {
      emailAddress.value = query.email as string;
      emailExists.value = true;
      activeSection.value = AuthSectionEnum.ResetPassword;
      header.value = "Reset your password";
    }
  },
  { immediate: true },
);
</script>

<template>
  <BaseModal @close="onClose" v-model:visible="show">
    <template #header>
      <h2 class="text-center text-[20px] font-semibold text-slate-800">
        {{ header }}
      </h2>
    </template>
    <component
      :is="screens[activeSection]"
      v-model:activeSection="activeSection"
      v-model:header="header"
      v-model:emailExists="emailExists"
      v-model:emailAddress="emailAddress"
      v-model:show="show"
    />
    <template #footer>
      <p class="mt-4 text-center text-[13px] text-slate-500">
        By continuing you agree and accept 4C Predictions<br />
        <router-link to="/terms-of-use" class="font-medium text-black underline"
          >Terms and Conditions</router-link
        >
        and
        <router-link to="/privacy-policy" class="font-medium text-black underline"
          >Privacy Policy</router-link
        >
      </p>
    </template>
  </BaseModal>
</template>
